/*
 * swatch-group.js — PDP swatch group (arm-sleeve pack groups, U4)
 *
 * Hand-written custom element. Deliberately depends on nothing inside the compiled
 * webpack bundles (global.js / main-product.js are IIFEs whose internals are not
 * reachable); the only externals it touches are `window.updatePst()` and the DOM
 * contract of `snippets/product-variants.liquid`.
 *
 * Navigation is a section-scoped fetch swap, structurally mirroring the FamilyVariants
 * element in assets/main-product.js (~L1836-1922). Its URL handling is NOT copied:
 * FamilyVariants fetches bare `products/<handle>` paths and strips `variant`, which is
 * the opposite of R6 (collection-aware URLs) and R19 (carry the selected size).
 *
 * Size carry (R19): main-product.js's variant-selects product-swap path
 * (`handleOptionValueChange` -> `buildRequestUrlWithParams`) carries `option_values=`,
 * whose members are option-value *IDs*. Those IDs are per-product, so they do not
 * resolve on an unrelated counterpart product. Instead we remember the current
 * selection by option NAME + VALUE, and after the swap re-check the matching input in
 * the freshly rendered section. That hands control straight back to the theme's own
 * variant-radios / variant-selects (which then runs its native
 * `optionValueSelectionChange` -> `buildRequestUrlWithParams` path). When the target has
 * no matching option value, nothing is selected — exactly R19's fallback.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('swatch-group')) return;

  var LOADING_ATTR = 'data-loading';
  var hasPushedState = false;

  function optionNameFromSelect(select) {
    var name = select.getAttribute('name') || '';
    var match = name.match(/^options\[(.+)\]$/);
    return match ? match[1] : null;
  }

  var SwatchGroup = class extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    // Starts from the parent, not `this`, so this lookup can never match the
    // <swatch-group> element itself. The block must never carry `data-section-id` (see
    // snippets/swatch-group.liquid) — if it did, this would resolve to the block instead
    // of the real section wrapper, and the fetch-swap below would nest the whole section
    // inside the block.
    get sectionElement() {
      var parent = this.parentElement;
      return parent ? parent.closest('[data-section-id]') : null;
    }

    onClick(event) {
      var link = event.target.closest ? event.target.closest('a[data-swatch-group-link]') : null;
      if (!link || !this.contains(link)) return;

      // Let the browser handle modified clicks (new tab/window, middle click).
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();

      if (link.getAttribute('aria-current') === 'true' || link.getAttribute('aria-disabled') === 'true') return;

      var href = link.getAttribute('href');
      if (!href) return;

      this.navigate(href);
    }

    navigate(targetUrl) {
      var section = this.sectionElement;
      var sectionId = section ? section.dataset.sectionId : null;

      if (!section || !sectionId || typeof window.fetch !== 'function') {
        window.location.assign(targetUrl);
        return;
      }

      // Double-tap guard: one navigation in flight at a time.
      if (this.getAttribute(LOADING_ATTR) === 'true') return;

      var selection = this.readSelection(section);
      var parts = targetUrl.split('?');
      var targetPath = parts[0];
      var params = new URLSearchParams(parts[1] || '');
      params.set('section_id', sectionId);

      var self = this;
      this.setLoading(true);

      window
        .fetch(targetPath + '?' + params.toString())
        .then(function (response) {
          if (!response.ok) throw new Error('swatch-group: HTTP ' + response.status);
          return response.text();
        })
        .then(function (responseText) {
          var parsed = new DOMParser().parseFromString(responseText, 'text/html');
          // Matches the first [data-section-id="<sectionId>"] element in the response. This
          // is only safe because <swatch-group> itself never carries data-section-id (see
          // sectionElement above and the block's Liquid) — otherwise this could match the
          // block instead of the real section wrapper it's nested inside.
          var incoming = parsed.querySelector('[data-section-id="' + sectionId + '"]');
          if (!incoming) throw new Error('swatch-group: section ' + sectionId + ' missing in response');

          section.innerHTML = incoming.innerHTML;

          try {
            window.history.pushState({ swatchGroup: true, url: targetUrl }, '', targetUrl);
            hasPushedState = true;
          } catch (error) {
            /* history is unavailable (e.g. sandboxed preview) — the swap still stands */
          }

          // Keep the theme's own variant machinery on the collection-aware path so its
          // replaceState (main-product.js `updateURL`) does not drop the collection
          // prefix on the next variant change (R6).
          self.retargetVariantElements(section, targetPath);
          self.applySelection(section, selection);

          if (typeof window.updatePst === 'function') window.updatePst();

          var focusTarget = section.querySelector('swatch-group a[aria-current="true"]');
          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });
          }
        })
        .catch(function () {
          // Error floor: never leave the shopper on a half-swapped page.
          window.location.assign(targetUrl);
        });
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.setAttribute(LOADING_ATTR, 'true');
        this.setAttribute('aria-busy', 'true');
      } else {
        this.removeAttribute(LOADING_ATTR);
        this.removeAttribute('aria-busy');
      }
    }

    /*
     * Reads the current option selection as {name, value} pairs.
     *
     * Linked (combined-listing) option values carry a non-empty `data-product-url`
     * pointing at a sibling product; re-applying one of those on the target would bounce
     * the shopper straight back. Those are skipped, as is the option this swatch row
     * itself stands in for (`data-linked-option-name`, the block's legend prefix).
     */
    readSelection(section) {
      var skipName = (this.dataset.linkedOptionName || '').toLowerCase();
      var selection = [];

      Array.prototype.forEach.call(
        section.querySelectorAll('input[type="radio"][name]:checked'),
        function (input) {
          if (input.dataset.productUrl) return;
          if (!input.name || input.name.toLowerCase() === skipName) return;
          selection.push({ name: input.name, value: input.value });
        }
      );

      Array.prototype.forEach.call(section.querySelectorAll('select[name^="options["]'), function (select) {
        var name = optionNameFromSelect(select);
        var option = select.options[select.selectedIndex];
        if (!name || !option || !select.value) return;
        if (option.dataset && option.dataset.productUrl) return;
        if (name.toLowerCase() === skipName) return;
        selection.push({ name: name, value: select.value });
      });

      return selection;
    }

    applySelection(section, selection) {
      if (!selection || !selection.length) return;

      selection.forEach(function (pair) {
        var radios = section.querySelectorAll('input[type="radio"][name]');
        var i;

        for (i = 0; i < radios.length; i++) {
          if (radios[i].name === pair.name && radios[i].value === pair.value) {
            if (!radios[i].checked) {
              radios[i].checked = true;
              radios[i].dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
          }
        }

        var selects = section.querySelectorAll('select[name^="options["]');
        for (i = 0; i < selects.length; i++) {
          if (optionNameFromSelect(selects[i]) !== pair.name) continue;
          var options = selects[i].options;
          for (var j = 0; j < options.length; j++) {
            if (options[j].value === pair.value) {
              selects[i].selectedIndex = j;
              selects[i].dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }
        }
        // No matching option value on the target: leave nothing selected (R19).
      });
    }

    retargetVariantElements(section, targetPath) {
      Array.prototype.forEach.call(
        section.querySelectorAll('variant-radios[data-url], variant-selects[data-url]'),
        function (element) {
          element.dataset.url = targetPath;
        }
      );
    }
  };

  customElements.define('swatch-group', SwatchGroup);

  // A pushState swap leaves the browser's back button pointing at a URL this page no
  // longer renders. Reload on the way back rather than showing a stale product.
  window.addEventListener('popstate', function () {
    if (hasPushedState) window.location.reload();
  });
})();
